import React, { memo, useCallback } from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EditMessageThreadCreateMessageUserIdListMutation,
  EditMessageThreadCreateMessageUserIdListMutationVariables,
} from "gql/graphql";
import { ReadOnlySlashIcon } from "modules/icons";
import EDIT_MESSAGE_THREAD_CREATE_MESSAGE_USER_ID_LIST_MUTATION from "modules/messaging/_networking/edit-message-thread-create-message-user-id-list";
import { useOverlay } from "modules/providers/overlay-provider";
import colors from "app/colors";
import { useMixpanel } from "../providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "../providers/mixpanel-provider/mixpanel-provider";
import ModalTitle from "./modal-title";

interface IProps {
  messageThreadId: string;
}

const ConfirmRemoveReadOnlyThreadModal = ({ messageThreadId }: IProps) => {
  const { closeModal } = useOverlay();
  const { track } = useMixpanel();

  const [editReadOnlyAPI] = useMutation<
    EditMessageThreadCreateMessageUserIdListMutation,
    EditMessageThreadCreateMessageUserIdListMutationVariables
  >(EDIT_MESSAGE_THREAD_CREATE_MESSAGE_USER_ID_LIST_MUTATION);

  const onRemoveClicked = useCallback(async () => {
    editReadOnlyAPI({
      variables: {
        id: messageThreadId,
        createMessageUserIdList: [],
      },
      optimisticResponse: {
        __typename: "Mutation",
        editMessageThread: {
          __typename: "MessageThread",
          id: messageThreadId,
          createMessageUserIdList: [],
        },
      },
    });
    track(MIXPANEL_EVENT_TYPE.READ_ONLY_CONVERSATION_TOGGLED, {
      "Read-Only Set": false,
      "New Conversation": false,
    });
    closeModal();
  }, [editReadOnlyAPI, messageThreadId, track, closeModal]);

  return (
    <Modal isOpen onClose={closeModal} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody p={6}>
          <VStack spacing={4}>
            <ModalTitle
              text="Remove  Read-Only Setting?"
              icon={<ReadOnlySlashIcon boxSize={16} />}
            />
            <Text
              textAlign="center"
              fontSize={14}
              fontWeight={500}
              color={colors.gray50}
            >
              Removing this setting will allow all users in this thread to post
              messages to it.
            </Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                Cancel
              </Button>
              <Button height={8} w={28} onClick={onRemoveClicked}>
                Remove
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(ConfirmRemoveReadOnlyThreadModal);
